import { useState } from "react";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import Label from "../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { IAddEditTableForm } from "../../../../helpers/appHelpers";
import APISelectField from "../../../../components/form_fields/APISelectField";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useUserStore from "../../../../state-management/useUserStore";
import { PATHS } from "../../../../routes/routes.paths";
import { EmployeeDto } from "../../../../models/employee.dto";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import { OvertimeDto } from "../../../../models/overtime.dto";
import {
	useCreateEmployeeOvertimeEntry,
	useUpdateEmployeeOvertimeEntry,
} from "../../../../services/employee-overtime-entry.service";

interface IFormInputs {
	employeeId: number;
	overtimeId: number;
	payPeriodId: number;
	numberOfHours: number;
}

type OvertimeManagementModalFormProps = IAddEditTableForm & {
	employees: EmployeeDto[];
	payPeriods: PayPeriodDto[];
	overtimeTypes: OvertimeDto[];
	reloadTableData?: () => void;
};

const OvertimeManagementModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	employees,
	payPeriods,
	overtimeTypes,
	reloadTableData,
}: OvertimeManagementModalFormProps) => {
	const { userType } = useUserStore();
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			employeeId: isEditForm ? formDataValues?.employeeId : "",
			overtimeId: isEditForm ? formDataValues?.overtimeId : "",
			payPeriodId: isEditForm ? formDataValues?.payPeriodId : "",
			numberOfHours: isEditForm ? formDataValues?.numberOfHours : "",
		},
	});

	const { mutate: createEmployeeOvertimeEntry } =
		useCreateEmployeeOvertimeEntry();
	const { mutate: updateEmployeeOvertimeEntry } =
		useUpdateEmployeeOvertimeEntry();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setUploading(true);

		if (isEditForm) {
			updateEmployeeOvertimeEntry(
				{ payload: data, id: formDataValues?.id },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				}
			);
		} else {
			createEmployeeOvertimeEntry(
				{
					payload: {
						...data,
						// companyId: currentCompany?.id,
					},
				},
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				}
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent open={open} title={title} handleClose={handleClose}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Employee" for="employeeId" />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.employeeId
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("employeeId", {
								required: "Select employee here",
								valueAsNumber: true,
							})}
						>
							<option value="">Select Employee</option>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.firstName + " " + employee.lastName}
								</option>
							))}
						</select>
						{errors.employeeId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.employeeId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Overtime" for="overtimeId" />
						<APISelectField
							key="overtimeId"
							attributes={{
								id: "overtimeId",
								defaultValue: isEditForm
									? formDataValues?.overtimeId
									: undefined,
							}}
							placeholder="Select Overtime"
							register={register}
							name="overtimeId"
							options={overtimeTypes}
							pathname={PATHS.OVERTIME_AND_BONUSES}
							required={"Select overtime here"}
							error={errors.overtimeId}
							doNotLinkToResource={userType === "HR"}
							valueAsNumber
						/>
						{errors.overtimeId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.overtimeId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Pay Period" for="payPeriodId" />
						<APISelectField
							key="payPeriodId"
							attributes={{
								id: "payPeriodId",
								defaultValue: isEditForm
									? formDataValues?.payPeriodId
									: undefined,
							}}
							placeholder="Select Pay Period"
							register={register}
							name="payPeriodId"
							options={payPeriods}
							mapText="code"
							pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
							required={"Select pay period here"}
							error={errors.payPeriodId}
							doNotLinkToResource={userType === "HR"}
							valueAsNumber
						/>
						{errors.payPeriodId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.payPeriodId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Number Of Hours" for="numberOfHours" />
						<input
							defaultValue={isEditForm ? formDataValues?.numberOfHours : ""}
							placeholder={"Enter value"}
							type="number"
							{...register("numberOfHours", {
								required: "Enter number of hours here",
							})}
							id="numberOfHours"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.numberOfHours
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.numberOfHours && (
							<p className="text-red-500 text-sm mt-1">
								{errors.numberOfHours.message}
							</p>
						)}
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: isEditForm
								? "Update Entry"
								: "Create Entry"
						}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default OvertimeManagementModalForm;
